// frontend/src/components/FileList.js
import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Select, MenuItem, Autocomplete, TextField, LinearProgress, IconButton } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { Link as MuiLink } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
// アップロード先の定義
const UPLOAD_TARGETS = [
  { type: 'project', label: '案件' },
  { type: 'customer', label: '顧客' },
  { type: 'order', label: '受注' },
  { type: 'contract', label: '契約' },
  { type: 'user', label: '個人（マイファイル）' }
];

export default function FileList({ currentUserId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [uploadType, setUploadType] = useState('user');
  const [uploadTargetId, setUploadTargetId] = useState(currentUserId);

  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [contracts, setContracts] = useState([]);

  // 一覧再取得
  const loadFiles = async () => {
    setLoading(true);
    try {
      const params = { related_type: uploadType };
      if (uploadType === 'user') params.related_id = currentUserId;
      else if (uploadTargetId) params.related_id = uploadTargetId;
      const res = await api.get('/api/files', { params });
      console.log('[loadFiles response]', res.data);
      setFiles(res.data?.files || res.data?.File || res.data || []);
    } catch (e) {
      console.error('[loadFiles API error]', e);
    }
    setLoading(false);
  };


  // マスタ取得
  useEffect(() => {
    api.get('/api/projects').then(res => setProjects(res.data));
    api.get('/api/customers').then(res => setCustomers(res.data));
    api.get('/api/orders').then(res => setOrders(res.data));
    api.get('/api/contracts').then(res => setContracts(res.data));
    api.get('/api/files').then(res => {
      console.log('[initial /api/files response]', res.data);
      setFiles(res.data?.files || res.data?.File || res.data || []);
    });
  }, []);

  // ターゲット変更時一覧再取得
  useEffect(() => {
    console.log('useEffect発火', { uploadType, uploadTargetId, currentUserId });
    if ((uploadType === 'user' && currentUserId) || uploadTargetId) {
      console.log('loadFiles呼び出し');
      loadFiles();
    } else {
      console.log('loadFilesスキップ');
    }
  }, [uploadType, uploadTargetId, currentUserId]);

  // D&D＋アップロード
  const onDrop = async (acceptedFiles) => {
    if (!uploadType || (!uploadTargetId && uploadType !== 'user')) return;
    setUploading(true);
    for (const file of acceptedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      if (uploadType !== 'user') {
        formData.append('related_type', uploadType);
        formData.append('related_id', uploadTargetId);
      }
      await api.post('/api/files', formData);
    }
    await loadFiles();
    setUploading(false);
  };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  // 削除・DL
  const handleDelete = async (id) => {
    if (!window.confirm('本当に削除しますか？')) return;
    await api.delete(`/api/files/${id}`);
    loadFiles();
  };
// ダウンロード（署名付きURL対応）
const handleDownload = async (file) => {
  try {
    // バックエンドで署名付きURLを返すようにした場合
    const res = await api.get(`/api/files/${file.id}`);
    if (res.data?.signedUrl) {
      window.open(res.data.signedUrl, '_blank');
    } else {
      alert('ダウンロードURLを取得できませんでした');
    }
  } catch (e) {
    console.error('[handleDownload error]', e);
    alert('ファイル取得に失敗しました');
  }
};


  const navigate = useNavigate();
  // カラム定義
  const columns = [
    { field: 'id', headerName: 'ID', width: 60 },
    {
      field: 'fileName',
      headerName: 'ファイル名',
      width: 200,
      renderCell: (params) =>
        <span
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => handleDownload(params.row)}
        >
          {params.row.fileName}
        </span>
    },
    { field: 'fileType', headerName: '種別', width: 90 },
    { field: 'version', headerName: 'Ver', width: 80 },
    {
      field: 'relatedType',
      headerName: 'アップロード先種別',
      width: 120,
      renderCell: (params) => {
        const label = UPLOAD_TARGETS.find(t => t.type === params.row.relatedType)?.label || params.row.relatedType;
        return <span>{label}</span>;
      }
    },
    {
      field: 'relatedId',
      headerName: 'アップロード先ID/名',
      width: 180,
      renderCell: (params) => {
        const { relatedType, relatedId } = params.row;
        // 自分のファイル
        if (relatedType === 'user' || !relatedId) return <span>マイファイル</span>;
        // 画面遷移先
        let url = '';
        if (relatedType === 'project') url = `/projects/${relatedId}`;
        else if (relatedType === 'customer') url = `/customers/${relatedId}`;
        else if (relatedType === 'order') url = `/orders/${relatedId}`;
        else if (relatedType === 'contract') url = `/contracts/${relatedId}`;
        // クリックで詳細画面遷移
        return (
          <MuiLink
            component="button"
            underline="hover"
            onClick={() => navigate(url)}
            sx={{ cursor: 'pointer' }}
          >
            {relatedId}
          </MuiLink>
        );
      }
    },
    {
      field: 'uploadedAt', headerName: 'アップロード日時', width: 160, valueFormatter: ({ value }) =>
        value ? value.slice(0, 19).replace('T', ' ') : ''
    },
    {
      field: 'actions', headerName: '', width: 100, renderCell: (params) => (
        <>
          <IconButton onClick={() => handleDownload(params.row)}><CloudDownloadIcon /></IconButton>
          <IconButton onClick={() => handleDelete(params.row.id)}><DeleteIcon /></IconButton>
        </>
      )
    },
  ];

  // アップロード先候補選択
  let uploadTargetSelect = null;
  if (uploadType === 'project') {
    uploadTargetSelect = (
      <Autocomplete
        options={projects}
        getOptionLabel={o => o.title || ''}
        value={projects.find(p => String(p.id) === String(uploadTargetId)) || null}
        onChange={(e, v) => setUploadTargetId(v ? v.id : null)}
        renderInput={params => <TextField {...params} label="案件を選択" />}
        sx={{ width: 220 }}
      />
    );
  } else if (uploadType === 'customer') {
    uploadTargetSelect = (
      <Autocomplete
        options={customers}
        getOptionLabel={o => o.name || ''}
        value={customers.find(c => String(c.id) === String(uploadTargetId)) || null}
        onChange={(e, v) => setUploadTargetId(v ? v.id : null)}
        renderInput={params => <TextField {...params} label="顧客を選択" />}
        sx={{ width: 220 }}
      />
    );
  } else if (uploadType === 'order') {
    uploadTargetSelect = (
      <Autocomplete
        options={orders}
        getOptionLabel={o => o.title || ''}
        value={orders.find(o => String(o.id) === String(uploadTargetId)) || null}
        onChange={(e, v) => setUploadTargetId(v ? v.id : null)}
        renderInput={params => <TextField {...params} label="受注を選択" />}
        sx={{ width: 220 }}
      />
    );
  } else if (uploadType === 'contract') {
    uploadTargetSelect = (
      <Autocomplete
        options={contracts}
        getOptionLabel={o => o.title || ''}
        value={contracts.find(c => String(c.id) === String(uploadTargetId)) || null}
        onChange={(e, v) => setUploadTargetId(v ? v.id : null)}
        renderInput={params => <TextField {...params} label="契約を選択" />}
        sx={{ width: 220 }}
      />
    );
  } else if (uploadType === 'user') {
    uploadTargetSelect = null;
    if (uploadTargetId !== currentUserId) setUploadTargetId(currentUserId);
  }

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>ファイルアップロード</Typography>

      {/* アップロード先選択 */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <Select
          value={uploadType}
          onChange={e => {
            setUploadType(e.target.value);
            setUploadTargetId(e.target.value === 'user' ? currentUserId : null);
          }}
          sx={{ minWidth: 130 }}
        >
          {UPLOAD_TARGETS.map(t => (
            <MenuItem value={t.type} key={t.type}>{t.label}</MenuItem>
          ))}
        </Select>
        {uploadTargetSelect}
      </Box>

      {/* D&Dドロップエリア */}
      <Box {...getRootProps()} sx={{
        p: 3, mb: 2, border: '2px dashed #bbb', borderRadius: 2,
        textAlign: 'center', bgcolor: isDragActive ? '#f0f0f0' : 'inherit',
        cursor: 'pointer'
      }}>
        <input {...getInputProps()} />
        {
          isDragActive
            ? <Typography>ここにファイルをドロップしてください</Typography>
            : <Typography>ここにファイルをドラッグ＆ドロップ、またはクリックで選択</Typography>
        }
        {uploading && <LinearProgress sx={{ mt: 1 }} />}
      </Box>

      <DataGrid
        rows={files}
        columns={columns}
        autoHeight
        pageSize={10}
        loading={loading}
        getRowId={row => row.id}
      />
    </Box>
  );
}
